import {useTranslation} from 'react-i18next'
import {SectionCard} from '@/components/section-card'
import {Tag} from '@/components/ui/tag'
import {DataTable, type Column} from '@/components/data-table'
import type {ValidezDatos} from '@/hooks/bateria/use-bateria-informe'

type FilaDatos = {id: number; celdas: readonly string[]}

function TablaConNota({
  titulo,
  origen,
  columnas,
  filas,
  nota,
  testId,
}: {
  titulo: string
  origen: 'derivado' | 'transcrito' | 'mixto'
  columnas: string[]
  filas: readonly (readonly string[])[]
  nota: string
  testId: string
}) {
  const {t} = useTranslation()
  const columns: Column<FilaDatos>[] = columnas.map((c, idx) => ({
    key: `col-${idx}`,
    header: c,
    cell: (fila) => fila.celdas[idx],
  }))
  const rows: FilaDatos[] = filas.map((celdas, id) => ({id, celdas}))
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-medium text-foreground">{titulo}</p>
        <Tag tone="neutral">{t(`admin.bateria.informe.validez.origen.${origen}`)}</Tag>
      </div>
      <div className="mt-2">
        <DataTable columns={columns} rows={rows} rowKey={(fila) => String(fila.id)} />
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">{nota}</p>
    </div>
  )
}

/** Bloque 7 (§P7): el mismo catálogo que hornea el DOCX (`bateria_reporte_validez`). Lo transcrito son
 * coeficientes del estudio de validación de 2010, no calculados sobre esta evaluación. */
export function ValidezConfiabilidad({validez}: {validez: ValidezDatos}) {
  const {t} = useTranslation()
  return (
    <SectionCard
      title={t('admin.bateria.informe.validez.title')}
      subtitle={t('admin.bateria.informe.validez.subtitle')}
      testId="informe-validez">
      <div className="space-y-6">
        <TablaConNota
          titulo={t('admin.bateria.informe.validez.intralaboralTitle')}
          origen="derivado"
          columnas={[
            t('admin.bateria.informe.validez.colDominio'),
            t('admin.bateria.informe.validez.colDimension'),
            t('admin.bateria.formaValue', {forma: 'A'}),
            t('admin.bateria.formaValue', {forma: 'B'}),
          ]}
          filas={validez.intralaboral_items}
          nota={validez.intralaboral_nota}
          testId="informe-validez-intralaboral"
        />
        <TablaConNota
          titulo={t('admin.bateria.informe.validez.extralaboralTitle')}
          origen="derivado"
          columnas={[t('admin.bateria.informe.validez.colDimension'), t('admin.bateria.informe.validez.colItems')]}
          filas={validez.extralaboral_items}
          nota={validez.extralaboral_nota}
          testId="informe-validez-extralaboral"
        />
        <TablaConNota
          titulo={t('admin.bateria.informe.validez.alfasGeneralesTitle')}
          origen="transcrito"
          columnas={[
            t('admin.bateria.informe.validez.colCuestionario'),
            'N',
            t('admin.bateria.informe.validez.colItems'),
            t('admin.bateria.informe.validez.colAlfa'),
            t('admin.bateria.informe.validez.colNivel'),
          ]}
          filas={validez.alfas_generales}
          nota={validez.alfas_generales_nota}
          testId="informe-validez-alfas-generales"
        />
        <TablaConNota
          titulo={t('admin.bateria.informe.validez.alfasExtralaboralTitle')}
          origen="mixto"
          columnas={[
            t('admin.bateria.informe.validez.colDimension'),
            t('admin.bateria.informe.validez.colItems'),
            t('admin.bateria.informe.validez.colAlfa'),
            t('admin.bateria.informe.validez.colNivel'),
          ]}
          filas={validez.alfas_extralaboral}
          nota={validez.alfas_extralaboral_nota}
          testId="informe-validez-alfas-extralaboral"
        />
      </div>
    </SectionCard>
  )
}
